/**
 * Offline mutation queue backed by IndexedDB.
 *
 * Handles:
 *  - Enqueue pending writes when offline (or always, for optimistic UI)
 *  - Flush queue on reconnect with optimistic concurrency (updated_at baseline)
 *  - Conflict detection → surfaces via event for a conflict modal
 *  - Dead-letter after 3 failed attempts
 *  - Multi-tenant isolation (userId + centerId namespace)
 */
import { openDB, type IDBPDatabase } from 'idb'
import { supabase } from '@/lib/supabase'
import { trackConversion } from '@/utils/analytics'

export type MutationType = 'attendance.upsert' | 'grade.upsert'

export interface QueuedMutation {
  id: string
  type: MutationType
  userId: string
  centerId: string
  clientId: string // unique per device
  payload: Record<string, any>
  // Parent scope used for conflict detection (eg sessionId / evaluationId)
  targetKey: string
  // Snapshot of `updated_at` from the server when the user started editing.
  // Used for optimistic concurrency at flush time.
  baselineUpdatedAt: string | null
  createdAt: string
  attempts: number
  lastError?: string
  status: 'pending' | 'syncing' | 'conflict' | 'dead'
}

const DB_NAME = 'antiplanning-offline'
const DB_VERSION = 1
const STORE = 'mutations'
const MAX_ATTEMPTS = 3

let dbPromise: Promise<IDBPDatabase> | null = null

function db() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'id' })
          store.createIndex('status', 'status')
          store.createIndex('userId', 'userId')
          store.createIndex('createdAt', 'createdAt')
        }
      },
    })
  }
  return dbPromise
}

// --- Client ID (per device) ---
const CLIENT_ID_KEY = 'antiplanning.clientId'
function getClientId(): string {
  let id = localStorage.getItem(CLIENT_ID_KEY)
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    localStorage.setItem(CLIENT_ID_KEY, id)
  }
  return id
}

// --- Events ---
export const OFFLINE_EVENTS = {
  queueChanged: 'offline-queue-changed',
  conflict: 'offline-conflict',
  syncStarted: 'offline-sync-started',
  syncCompleted: 'offline-sync-completed',
} as const

function emit(name: string, detail?: any) {
  window.dispatchEvent(new CustomEvent(name, { detail }))
}

// --- Public API ---

export interface EnqueueArgs {
  type: MutationType
  userId: string
  centerId: string
  targetKey: string
  baselineUpdatedAt: string | null
  payload: Record<string, any>
}

export async function enqueueMutation(args: EnqueueArgs): Promise<QueuedMutation> {
  const now = new Date().toISOString()
  const mutation: QueuedMutation = {
    id: `${args.type}-${args.targetKey}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type: args.type,
    userId: args.userId,
    centerId: args.centerId,
    clientId: getClientId(),
    payload: args.payload,
    targetKey: args.targetKey,
    baselineUpdatedAt: args.baselineUpdatedAt,
    createdAt: now,
    attempts: 0,
    status: 'pending',
  }
  const idb = await db()
  await idb.put(STORE, mutation)
  emit(OFFLINE_EVENTS.queueChanged)
  try { trackConversion('offline_mutation_created', { feature: args.type }) } catch {}
  return mutation
}

export async function clearDeadFor(userId: string): Promise<number> {
  const idb = await db()
  const all = await idb.getAll(STORE) as QueuedMutation[]
  let count = 0
  for (const m of all) {
    if (m.userId === userId && m.status === 'dead') {
      await idb.delete(STORE, m.id)
      count++
    }
  }
  if (count > 0) emit(OFFLINE_EVENTS.queueChanged)
  return count
}

export async function listMutations(filter?: { userId?: string; status?: QueuedMutation['status'] }): Promise<QueuedMutation[]> {
  const idb = await db()
  const all = await idb.getAll(STORE) as QueuedMutation[]
  return all
    .filter(m => !filter?.userId || m.userId === filter.userId)
    .filter(m => !filter?.status || m.status === filter.status)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

export async function countPending(userId?: string): Promise<number> {
  const list = await listMutations({ userId, status: 'pending' })
  return list.length
}

export async function removeMutation(id: string): Promise<void> {
  const idb = await db()
  await idb.delete(STORE, id)
  emit(OFFLINE_EVENTS.queueChanged)
}

export async function clearAllFor(userId: string): Promise<void> {
  const idb = await db()
  const all = await idb.getAll(STORE) as QueuedMutation[]
  for (const m of all) {
    if (m.userId === userId) await idb.delete(STORE, m.id)
  }
  emit(OFFLINE_EVENTS.queueChanged)
}

export async function updateMutation(id: string, patch: Partial<QueuedMutation>): Promise<void> {
  const idb = await db()
  const current = await idb.get(STORE, id) as QueuedMutation | undefined
  if (!current) return
  const next = { ...current, ...patch }
  await idb.put(STORE, next)
  emit(OFFLINE_EVENTS.queueChanged)
}

// --- Flush logic ---

export interface ConflictInfo {
  mutation: QueuedMutation
  serverState: any
}

/**
 * Executes a single mutation against Supabase with optimistic concurrency.
 * Returns:
 *  - 'success'     → mutation applied; caller should remove it from the queue
 *  - 'conflict'    → server has a newer version; caller should surface to user
 *  - 'retry'       → transient error; try again later
 *  - 'fatal'       → non-recoverable (RLS, schema mismatch) → dead-letter
 */
export async function flushOne(m: QueuedMutation): Promise<'success' | 'conflict' | 'retry' | 'fatal'> {
  try {
    if (m.type === 'attendance.upsert') {
      return await flushAttendance(m)
    }
    if (m.type === 'grade.upsert') {
      return await flushGrade(m)
    }
    return 'fatal'
  } catch (err: any) {
    console.error('[offlineQueue] flushOne error', err)
    const message = err?.message || String(err)
    // Distinguish network from logic errors
    if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
      return 'retry'
    }
    // Auth expired
    if (err?.status === 401 || message.includes('JWT') || message.includes('expired')) {
      return 'retry'
    }
    return 'fatal'
  }
}

async function flushAttendance(m: QueuedMutation): Promise<'success' | 'conflict' | 'retry' | 'fatal'> {
  const rows = m.payload.rows as any[]
  if (!Array.isArray(rows) || rows.length === 0) return 'fatal'

  // For each row, attempt upsert. We do it row-by-row to detect conflicts per student.
  // An upsert without an OCC check could silently overwrite other teachers' edits.
  for (const row of rows) {
    // Fetch current server state for this (session_id, student_id)
    const { data: current, error: fetchErr } = await supabase
      .from('session_attendance')
      .select('updated_at, status, late_minutes, excuse_reason, notes, marked_by')
      .eq('session_id', row.session_id)
      .eq('student_id', row.student_id)
      .maybeSingle()

    if (fetchErr) {
      console.error('[offlineQueue] attendance fetch error', fetchErr)
      return 'retry'
    }

    // If the row exists and has been updated since we started editing, it's a conflict
    if (current && m.baselineUpdatedAt && current.updated_at && current.updated_at > m.baselineUpdatedAt) {
      // Check if the change is semantically identical → no real conflict
      const sameValue =
        current.status === row.status &&
        (current.late_minutes || null) === (row.late_minutes || null) &&
        (current.excuse_reason || null) === (row.excuse_reason || null) &&
        (current.notes || null) === (row.notes || null)
      if (!sameValue) {
        emit(OFFLINE_EVENTS.conflict, {
          mutation: m,
          serverState: current,
          row,
        } as ConflictInfo)
        return 'conflict'
      }
    }

    // Apply upsert
    const { error: upsertErr } = await supabase
      .from('session_attendance')
      .upsert([row], { onConflict: 'session_id,student_id' })

    if (upsertErr) {
      console.error('[offlineQueue] attendance upsert error', upsertErr)
      // 401/403 or RLS → fatal (can't be retried with same token)
      if (upsertErr.code === '42501' || (upsertErr as any).status === 403) return 'fatal'
      return 'retry'
    }
  }
  return 'success'
}

async function flushGrade(m: QueuedMutation): Promise<'success' | 'conflict' | 'retry' | 'fatal'> {
  const rows = m.payload.rows as any[]
  if (!Array.isArray(rows) || rows.length === 0) return 'fatal'

  for (const row of rows) {
    const { data: current, error: fetchErr } = await supabase
      .from('grades')
      .select('updated_at, grade, is_absent, comment, graded_by')
      .eq('evaluation_id', row.evaluation_id)
      .eq('student_id', row.student_id)
      .maybeSingle()

    if (fetchErr) return 'retry'

    if (current && m.baselineUpdatedAt && current.updated_at && current.updated_at > m.baselineUpdatedAt) {
      const sameValue =
        (current.grade == null ? null : Number(current.grade)) === (row.grade == null ? null : Number(row.grade)) &&
        current.is_absent === row.is_absent &&
        (current.comment || null) === (row.comment || null)
      if (!sameValue) {
        emit(OFFLINE_EVENTS.conflict, {
          mutation: m,
          serverState: current,
          row,
        } as ConflictInfo)
        return 'conflict'
      }
    }

    const { error: upsertErr } = await supabase
      .from('grades')
      .upsert([row], { onConflict: 'evaluation_id,student_id' })

    if (upsertErr) {
      if (upsertErr.code === '42501') return 'fatal'
      return 'retry'
    }
  }
  return 'success'
}

/**
 * Flushes the entire pending queue for a user, FIFO.
 * Stops on the first conflict (user must resolve before continuing).
 * Returns the number of successful mutations.
 */
export async function flushQueue(userId: string): Promise<{ success: number; conflicts: number; dead: number }> {
  emit(OFFLINE_EVENTS.syncStarted)
  const pending = await listMutations({ userId, status: 'pending' })
  let success = 0
  let conflicts = 0
  let dead = 0

  for (const m of pending) {
    await updateMutation(m.id, { status: 'syncing' })
    const result = await flushOne(m)
    if (result === 'success') {
      await removeMutation(m.id)
      success++
    } else if (result === 'conflict') {
      await updateMutation(m.id, { status: 'conflict' })
      conflicts++
      try { trackConversion('offline_conflict_detected', { feature: m.type }) } catch {}
      break // stop on first conflict — user must resolve
    } else if (result === 'retry') {
      const attempts = m.attempts + 1
      if (attempts >= MAX_ATTEMPTS) {
        await updateMutation(m.id, { status: 'dead', attempts, lastError: 'Too many retries' })
        dead++
      } else {
        await updateMutation(m.id, { status: 'pending', attempts, lastError: 'Retry' })
      }
    } else {
      // fatal
      await updateMutation(m.id, { status: 'dead', lastError: 'Fatal error' })
      dead++
    }
  }

  emit(OFFLINE_EVENTS.syncCompleted, { success, conflicts, dead })
  try {
    if (success > 0) trackConversion('offline_sync_success', { value: success })
    if (dead > 0) trackConversion('offline_sync_dead_letter', { value: dead })
  } catch {}
  return { success, conflicts, dead }
}

/**
 * Force-resolve a conflict by pushing the client value regardless of server state.
 */
export async function resolveConflictKeepMine(mutationId: string): Promise<void> {
  const idb = await db()
  const m = await idb.get(STORE, mutationId) as QueuedMutation | undefined
  if (!m) return
  // Bypass baseline check
  await updateMutation(mutationId, { baselineUpdatedAt: null, status: 'pending', attempts: 0 })
}

/**
 * Resolve conflict by discarding the client mutation.
 */
export async function resolveConflictUseTheirs(mutationId: string): Promise<void> {
  await removeMutation(mutationId)
}
